import React, { useEffect, useState, memo, useRef } from 'react';
import { container } from "tsyringe";
import { useDispatch, useSelector } from 'react-redux';
import { selectToolAction } from '../../redux/layout/layoutActions';
import { ToolsType } from '../../models/layoutClass';
import { People } from '../../models/peopleClass';


import { PeopleService, PeopleKind } from '../../services/people-service';
import { PTApiService } from '../../services/ptapi-service';
import { UserService } from '../../services/user-service';
import { WebRTCsdkService } from '../../services/webrtcsdk-service';

import Box from '@mui/material/Box';
import Badge from '../badge/Badge';
import SearchBox from '../search/SearchBox';
import InlineEdit from '../utils/InlineEdit';
import PeopleMenu from '../../components/submenu-people/PeopleMenu';
import useOnClickOutside from '../hooks/useOnClickOutside';

import { StyledTab, StyledTabs, TabPanel } from '../tabs/StyledTabs';

import { getInitials, useSharedState, getBadgeColor, format } from '../../utils/helpers';

import "./PeopleRoster.scss";
import { MEETING_SET_MENU_PEOPLE } from '../../redux/meeting/meetingReducer';
import { LAYOUT_SELECT_TOOL } from '../../redux/layout/layoutReducer';
import { SoundKey, SoundService } from '../../services/sound-service';
import { useNotTooFast } from '../hooks/useNotTooFast';
import { ChatService } from '../../services/chat-service';
import { MeetingConnectionState } from '../../models/meetingClass';
import {ProducerType} from '../../models/producerClass';


export interface ParticipantProp {
  people: People;
}

export interface ExtraLineProps {
  people: People;
  onClose: any;
}

const CnxDetails = (props: ExtraLineProps) => {

  const { people, onClose } = props;

  const cnxDetailsRef = useRef(undefined);
  useOnClickOutside(cnxDetailsRef, (event) => {
    console.log('MENU remove closed', event.target.id)
    onClose(event);
  });

  return (
    <div className='container-details' ref={cnxDetailsRef}>
      <div className='close' onClick={() => onClose(null)}></div>
      <div className="title">Connection Details</div>
      <table width="100%"><tbody>
        <tr>
          <td width="30%" style={{ textAlign: "right" }}>Browser</td>
          <td colSpan={2} width="70%" style={{ textAlign: 'left' }}>{format(people.endpointDetail?.browser) + ' / ' + format(people.endpointDetail?.os)}</td>
        </tr>
        <tr>
          <th></th>
          <th>Receiving</th>
          <th>Sending</th>
        </tr>
        <tr>
          <td style={{ textAlign: "right" }}>Resolution</td>
          <td>{format(people.properties?.videoRecvWidth) + 'x' + format(people.properties?.videoRecvHeight)}</td>
          <td>{format(people.properties?.videoSendWidth) + 'x' + format(people.properties?.videoSendHeight)}</td>
        </tr>
        <tr>
          <td style={{ textAlign: "right" }}>Video</td>
          <td>{format(people.properties?.videoRecvCodec)}</td>
          <td>{format(people.properties?.videoSendCodec)}</td>
        </tr>
        <tr>
          <td style={{ textAlign: "right" }}>Audio</td>
          <td>{format(people.properties?.audioRecvCodec)}</td>
          <td></td>
        </tr>
      </tbody></table>
    </div>
  )
}

const RemoveParticipant = (props: ExtraLineProps) => {

  const { people, onClose } = props;
  const [ptApiService] = useState(container.resolve(PTApiService));
  const [soundService] = useState(container.resolve(SoundService));
  const [peopleService] = useState(container.resolve(PeopleService));

  const removeRef = useRef(undefined);
  useOnClickOutside(removeRef, (event) => {
    console.log('MENU remove closed', event.target.id)
    onClose(event);
  });

  const cancelRemove = () => {
    onClose(null);
  }
  const confirmRemove = () => {
    onClose(null);

    // ptApiService.kickoutParticipant(people.endpointGUID, people.role);
    peopleService.kickoutParticipant(people.endpointGUID, people.role);
    soundService.playSoundStart(SoundKey.Trash)
  }

  return (
    <div className='removal-line' ref={removeRef}>
      <div className='question'>
        Do you want to remove&nbsp;
        <div className='name'>{people.name}</div>
        &nbsp;?
      </div>
      <div className='action-no' onClick={cancelRemove}>No</div>
      <div className='action-yes' onClick={confirmRemove}>YES, REMOVE</div>
    </div>
  )

}


const Participant = memo(function ParticipantFn(props: ParticipantProp) {

  const dispatch = useDispatch();
  const selectedPeople = useSelector((state: any) => state.meetingStore.selectedMenuPeople);

  const { people } = props;

  const [userService] = useState(container.resolve(UserService));
  const [ptApiService] = useState(container.resolve(PTApiService));
  const [chatService] = useState(container.resolve(ChatService));
  const [webRTCsdkService] = useState(container.resolve(WebRTCsdkService));
  const [peopleService] = useState(container.resolve(PeopleService));

  const profilePictureUrl = 'url(' + userService.profilePicture + ')';

  const initials = people.name.length > 0 ? getInitials(people.name) : getInitials(people.name);
  const bgcolor = getBadgeColor(people.id);  // use a constant

  const [isMenuOpened, setMenuOpened] = useState(false);
  const [isRenaming, setRenaming] = useState(false);
  const [isRemoving, setRemoving] = useState(false);
  const [isCnxDetails, setCnxDetails] = useState(false);

  const [lastClosedTime, setLastClosedTime] = useState(0);

  const [userName, setUserName] = useState(people.name);

  const checkNotTooFast = useNotTooFast(800);

  const handleSetName = async (newName) => {
    // change moderator/presenter name
    if (newName.length > 0) {
      // await ptApiService.renameParticipantByGuid(newName.trim(), people.endpointGUID);
      peopleService.renameParticipantByGuid(newName.trim(), people.endpointGUID);
    } else {
      // reset old name
      setUserName(people.name);
    }
    setRenaming(false);
  }

  const toggleAudioMute = () => {
    if (checkNotTooFast() && !people.audioHardMute) {
      // ptApiService.setParticipantMute(people.endpointGUID, 'audio', !people.audioMute, false);
      peopleService.setParticipantMute(people.endpointGUID, 'audio', !people.audioMute, false);
      if (people.isSelf) {
        // this is ourSelf
        webRTCsdkService.setAudioMuted(!people.audioMute);
      }
    }
  }
  const toggleVideoMute = () => {
    if (checkNotTooFast() && !people.videoHardMute) {
      // ptApiService.setParticipantMute(people.endpointGUID, 'video', !people.videoMute, false);
      peopleService.setParticipantMute(people.endpointGUID, 'video', !people.videoMute, false);
      if (people.isSelf) {
        // this is ourSelf
        webRTCsdkService.setVideoMuted(!people.videoMute);
      }

    }
  }

  const handleClose = () => {
    console.log('MENU close')
    const now = Date.now();
    setLastClosedTime(now);
    setMenuOpened(false);
    setCnxDetails(false);
    setRemoving(false);
  }

  const openMenu = () => {
    setMenuOpened(true);
  }

  const handleToggleMenu = () => {
    console.log('MENU toggle')
    const now = Date.now();
    if (now - lastClosedTime > 500) {
      setMenuOpened(!isMenuOpened);
    } else {
      console.log('MENU toofast')
    }
    setLastClosedTime(now);
  }


  const handleRename = () => {
    setMenuOpened(false);
    setRenaming(true);
  }
  const handleRemove = () => {
    setMenuOpened(false);
    setRemoving(true);
  }
  const handleDetails = () => {
    setMenuOpened(false);
    setCnxDetails(true);
  }

  const handleChat = (endpointGuid: string) => {
    // open ChatHost on direct message chat
    chatService.currentOneToOneEndpointGuid$.next(endpointGuid);
    localStorage.setItem('hostChatSelectedTab', '2');
    dispatch(selectToolAction(ToolsType.HOSTCHAT));
  }

  const closeRemoving = (event) => {
    setRemoving(false);
    if (event?.target && event.target.classList.contains('menu')) {
      event.target.click();
    }
  }
  const closeCnxDetails = (event) => {
    setCnxDetails(false);
    if (event?.target && event.target.classList.contains('menu')) {
      event.target.click();
    }
  }

  useEffect(() => {
    console.log('got an update on PEOPLE', people.name);
    if (userName !== people.name) {
      setUserName(people.name);
      setRenaming(false);
    }
  }, [people]);


  return (
    <>
      <div className="participant-line">
        {people.isSelf && profilePictureUrl !== 'url(null)' ?
          <div className="user-picture" style={{ backgroundImage: profilePictureUrl }}></div>
          :
          <div className="user-initials" style={{ backgroundColor: bgcolor }}>{initials}</div>
        }

        {people.role !== PeopleKind.ATTENDEE ?
          <>
            <div className="username">
              <InlineEdit
                width={216}
                height={18}
                key={userName}
                text={userName}
                focused={isRenaming}
                placeHolder="&middot;&middot;&middot;"
                onDiscard={() => setRenaming(false)}
                onSetText={(text) => handleSetName(text)}
              />
              {people.isSelf &&
                <div className="myself">(me)</div>
              }
              {people.role === 'Moderator' &&
                <div className="moderator"></div>
              }
            </div>

            <div className='actions'>
              <div className="menu" id={'menu-' + people.name} onClick={() => handleToggleMenu()}>
              </div>
              {
                <>
                  <div className={"video video-" + (people.videoHardMute ? "hard" : (people.videoMute ? "off" : "on"))} onClick={() => toggleVideoMute()}></div>
                  <div className={"mic mic-" + (people.audioHardMute ? "hard" : (people.audioMute ? "off" : "on"))} onClick={() => toggleAudioMute()}></div>
                </>
              }
            </div>
          </>
          :
          <>
            <div className="username">
              {people.name}
            </div>

            <div className='actions'>
              <div className="menu" id={'menu-' + people.id} onClick={() => handleToggleMenu()}>
              </div>
            </div>
          </>
        }
      </div>
      {isRemoving &&
        <RemoveParticipant people={people} onClose={(e) => closeRemoving(e)}></RemoveParticipant>
      }
      {isCnxDetails &&
        <CnxDetails people={people} onClose={(e) => closeCnxDetails(e)}></CnxDetails>
      }

      {isMenuOpened &&
        <>
          {
            people.role !== PeopleKind.ATTENDEE ?
              <PeopleMenu people={people} onClose={handleClose} onRename={handleRename} onRemove={handleRemove} onDetails={handleDetails} ></PeopleMenu>
              :
              <PeopleMenu people={people} onClose={handleClose} onRemove={handleRemove} onChat={() => handleChat(people.endpointGUID)}></PeopleMenu>
          }
        </>
      }
    </>
  )
})

interface PeopleProps {
  kind: string;
}

const PeopleTab = (props: PeopleProps) => {

  const { kind } = props;

  const sortContainerRef = useRef(undefined);
  const [sortClickedDate, setSortClickedDate] = useState(0);

  const [peopleService] = useState(container.resolve(PeopleService));
  const [ptApiService] = useState(container.resolve(PTApiService));
  const [webRTCsdkService] = useState(container.resolve(WebRTCsdkService));

  // listen to meetingRoster
  const [peoples] = useSharedState(peopleService.peoples$);

  const [count, setCount] = useState(0);
  const [searchFilteredList, setSearchFilteredList] = useState([]);
  const [isAllMuted, setAllMuted] = useState(false);
  const [isAlphaSorted, setAlphaSorted] = useState(false);
  const [sortOrder, setSortOrder] = useState(1);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const [searchText, setSearchText] = useState('');

  const [meetingCnxState, setMeetingCnxState] = useState(MeetingConnectionState.IDLE);

  const [meetingState] = useSharedState(webRTCsdkService.meetingState$);
  const [participants] = useSharedState(webRTCsdkService.participants$);


  useEffect(() => {
    console.log('Call state', meetingState);
    setMeetingCnxState(meetingState.connexionState);
    // if (meetingState.connexionState !== MeetingConnectionState.CONNECTED) {
    //   // force mute audio/video

    // }
  }, [meetingState]);

  useEffect(() => {
    console.log('participants', participants);
    // get count of participants
    ptApiService.getAttendeeCount().then(({ attendeeCount, interactiveCount }) => {
      console.log('Attendee count', attendeeCount);
      console.log('Interactive count', interactiveCount);
    });
  }, [participants]);


  useOnClickOutside(sortContainerRef, () => {
    setShowSortMenu(false);
    setSortClickedDate(Date.now());
  });


  const onSearchUpdate = (value) => {
    console.log('search value', value);
    setSearchText(value);
  }

  const selectSortMenu = () => {
    const now = Date.now();
    if (now - sortClickedDate > 500) {
      setShowSortMenu(!showSortMenu);
    }
    setSortClickedDate(now);
  }

  const toggleSortOrder = (e) => {
    if (e) e.stopPropagation();
    setSortOrder(-1 * sortOrder);
  }

  const toggleMuteAll = () => {
    // ptApiService.setPresentersMute(!isAllMuted);
    peopleService.setPresentersMute(!isAllMuted);
    setAllMuted(!isAllMuted);
  }

  const selectSortKind = (e, kind) => {
    if (e) e.stopPropagation();
    setAlphaSorted(kind === 'alpha');
    setShowSortMenu(false);
  }

  const alphaSort = (a, b) => {
    if (a.name < b.name) return -1 * sortOrder;
    return sortOrder;
  }

  const natSort = (a, b) => {
    if (a.joinTime < b.joinTime) return -1 * sortOrder;
    return sortOrder;
  }

  useEffect(() => {
    console.log('new peopleRoster received for display');
    if (peoples && peoples.length > 0) {

      let filtered: People[] = peoples.filter(p => (kind === PeopleKind.ATTENDEE) ? (p.role === PeopleKind.ATTENDEE) : (p.role !== PeopleKind.ATTENDEE && p.inParticipantRoster));

      setCount(filtered.length);

      let searchFiltered = filtered;
      if (searchText && searchText.length > 0) {
        searchFiltered = filtered.filter(p => p.name.toLowerCase().indexOf(searchText.toLowerCase()) >= 0);
      }

      searchFiltered.sort(isAlphaSorted ? alphaSort : natSort);
      setSearchFilteredList(searchFiltered);
    }
  }, [peoples, searchText, sortOrder, isAlphaSorted]);


  return (
    <div className="peopleRoster">
      {count === 0 ?
        <div className="empty">
          <div className="empty empty-icon"></div>
          <div>No {kind === PeopleKind.ATTENDEE ? 'Attendees' : 'Presenters'} have joined this event yet!</div>
        </div>
        :
        <>
          <SearchBox text={searchText} placeholder={'Search...'} onChangeText={onSearchUpdate}></SearchBox>
          <div className='action-line'>
            {kind !== PeopleKind.ATTENDEE ?
              <div className={isAllMuted ? 'btn-unmute' : 'btn-mute'} onClick={toggleMuteAll}>
                {isAllMuted ? 'Unmute All' : 'Mute All'}</div>
              : <></>
            }
            <div className={(isAlphaSorted ? 'btn-sort-alpha' : 'btn-sort-nat') + (sortOrder == 1 ? '' : '-desc')} onClick={selectSortMenu}>
              Sort
              <div className="btn-sort-order" onClick={(e) => toggleSortOrder(e)}>
                {showSortMenu &&
                  <div className="sort-container" ref={sortContainerRef}>
                    <div className={"choice alpha" + (sortOrder == 1 ? '' : '-desc') + (isAlphaSorted ? ' selected' : '')} onClick={(e) => selectSortKind(e, 'alpha')}>Alphabetical View</div>
                    <div className={"choice time" + (sortOrder == 1 ? '' : '-desc') + (!isAlphaSorted ? ' selected' : '')} onClick={(e) => selectSortKind(e, 'time')}>Event Arrival View</div>
                  </div>
                }
              </div>
            </div>
          </div>
          <ul>
            {
              searchFilteredList.map(p =>
                { p.type !== ProducerType.CONTENT_SHARING &&
                  <li className="participant" key={p.id} id={'people-' + p.id}
                  >
                    <Participant people={p}></Participant>
                  </li>
                }
              )
            }
          </ul>


        </>
      }


      {/* <div className="form-container" style={{textAlign: 'left'}}>
        <br />
        <div className="status">{meetingCnxState}</div>
        <br />
        {meetingCnxState === MeetingConnectionState.CONNECTED &&
          <>
            <ul>
              {
                participants.map(p =>
                  <li className="rawparticipant" key={p.particiantGuid ? p.participant.Guid : (Math.floor(Math.random() * 10000)).toString()}>
                    {p.name} {'self=' + p.isSelf} {'mo=' + p.isModerator} {'vimute=' + p.isVideoMuted} {'aumute=' + p.isAudioMuted} {'sharing=' + p.isSharing}
                  </li>)
              }
            </ul>
          </>
        }
      </div> */}


    </div>
  )
}


const PeopleRoster = () => {

  const [peopleService] = useState(container.resolve(PeopleService));


  const selectedPeopleTab = parseInt(localStorage.getItem('selectedPeopleTab'))
  const [value, setValue] = useState(isNaN(selectedPeopleTab) ? 0 : selectedPeopleTab)


  const [counters, setCounters] = useState({ presenters: 0, attendees: 0 })
  const [peoples] = useSharedState(peopleService.peoples$);


  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    localStorage.setItem('selectedPeopleTab', newValue.toString());
  }

  useEffect(() => {
    console.log('new peopleRoster received for count');
    if (peoples && peoples.length > 0) {
      const attendees = peoples.filter(p => p.role === PeopleKind.ATTENDEE).length;
      setCounters({ presenters: peoples.length - attendees, attendees })
    }
  }, [peoples]);

  return (
    <>
      <Box sx={{ bgcolor: '#ffffff', width: '100%' }}>
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label="People category"
        >
          <StyledTab label={<><div className="label">Presenters</div><Badge counter={counters.presenters} top={24} left={160} lineHeight={20} background={" linear-gradient(315deg, #c94bf9, #55adde)"}></Badge></>} />
          <StyledTab label={<><div className="label">Attendees</div><Badge counter={counters.attendees} top={24} left={160} lineHeight={20} background={" linear-gradient(315deg, #c94bf9, #55adde)"}></Badge></>} />
        </StyledTabs>
      </Box>

      <TabPanel value={value} index={0}>
        <PeopleTab kind={PeopleKind.PRESENTER}></PeopleTab>
      </TabPanel>
      <TabPanel value={value} index={1}>
        <PeopleTab kind={PeopleKind.ATTENDEE}></PeopleTab>
      </TabPanel>

    </>
  );
}

export default PeopleRoster;